import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/modern/components/ui/dialog-shadcn';
import { Button } from '@/modern/components/ui/button';
import { Checkbox } from '@/modern/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/modern/components/ui/radio-group';
import { Label } from '@/modern/components/ui/label';
import { ScrollArea } from '@/modern/components/ui/scroll-area';
import { GRANTS } from '@/other/constants/PrivilegeTypes';
import { copyEntityToOtherBoard } from '@/network/network';
import { cn } from '@/modern/utils';
import { toast } from 'sonner';
import BryntumContext from '@/react/context/bryntumContext';
import { DuplicateEntityResolutionDialog } from './DuplicateEntityResolutionDialog';
import type { CopyTaskResponse } from '@/types/CopyTask';
import { capitalizeText } from '@/other/utils/utils';

interface CopyToBoardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record: any;
  entityType: string;
  entityName?: string;
}

export const CopyToBoardDialog: React.FC<CopyToBoardDialogProps> = ({
  open,
  onOpenChange,
  record,
  entityType,
  entityName,
}) => {
  const { boardManager, selectedBoard } = useContext(BryntumContext);
  const [selectedBoardId, setSelectedBoardId] = useState<number | string | null>(null);
  const [copyMetadata, setCopyMetadata] = useState(false);
  const [archiveOriginal, setArchiveOriginal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Duplicate resolution state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedBoardName, setSelectedBoardName] = useState<string>('');

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedBoardId(null);
      setCopyMetadata(false);
      setArchiveOriginal(false);
      setShowDuplicateModal(false);
      setSelectedBoardName('');
    }
  }, [open]);

  // Get boards with copy access (grant_four)
  const availableBoards = useMemo(() => {
    //@ts-expect-error boardManager is not typed in BryntumContext.
    if (!boardManager?.boardNameStore?.records) return [];

    //@ts-expect-error boardManager is not typed in BryntumContext.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return boardManager.boardNameStore.records.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (board: any) =>
        board.id !== selectedBoard?.id &&
        board.grants?.includes(GRANTS.grant_four)
    );
  }, [boardManager, selectedBoard]);

  const handleCopy = async () => {
    if (!selectedBoardId) return;

    setIsLoading(true);
    try {
      const response = await copyEntityToOtherBoard(entityType, record.id, selectedBoardId, {
        action: 'create_new',
        copy_metadata: copyMetadata,
        [`archive_original_${entityType}`]: archiveOriginal,
      });

      const data = response.data as CopyTaskResponse;

      if (data.duplicate_exists) {
        // Duplicate detected - show resolution modal
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const board = availableBoards.find((b: any) => b.id === selectedBoardId);
        setSelectedBoardName(board?.name || 'selected board');
        setShowDuplicateModal(true);
      } else {
        // Success - no duplicate
        // Archive the original entity if option was selected
        if (archiveOriginal && record) {
          record.archived = true;
        }
        toast.success(`${capitalizeText(entityType)} copied successfully`);
        onOpenChange(false);
      }
    } catch (error) {
      toast.error('Sorry, something went wrong. Unable to copy!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateResolve = async (action: 'replace' | 'duplicate', entityName?: string) => {
    if (!selectedBoardId) return;

    try {
      await copyEntityToOtherBoard(entityType, record.id, selectedBoardId, {
        action,
        [`${entityType}_name`]: entityName,
        copy_metadata: copyMetadata,
        [`archive_original_${entityType}`]: archiveOriginal,
      });

      // Archive the original entity if option was selected
      if (archiveOriginal && record) {
        record.archived = true;
      }

      toast.success(`${capitalizeText(entityType)} copied successfully`);
      setShowDuplicateModal(false);
      onOpenChange(false);
    } catch (error) {
      toast.error('Sorry, something went wrong. Unable to copy!');
      throw error; // Re-throw to keep the duplicate modal open
    }
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setSelectedBoardName('');
  };

  const displayName = entityName || record?.name || 'this entity';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal>
        <DialogContent className="sm:max-w-[600px]" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl">
              Copy to board
            </DialogTitle>
            <DialogDescription>
              Select the board you want to copy <span className="font-semibold">"{displayName}"</span> to.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {availableBoards.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <i className="fa-light fa-inbox fa-3x mb-4 block opacity-20" />
                <p className="text-base font-medium">No boards available</p>
                <p className="text-sm mt-1">You need edit access to other boards.</p>
              </div>
            ) : (
              <>
                {/* Board List - Radio Buttons */}
                <ScrollArea className="h-[350px] pr-3">
                  <RadioGroup
                    value={selectedBoardId?.toString()}
                    onValueChange={(value) => setSelectedBoardId(value)}
                  >
                    <div className="space-y-2">
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {availableBoards.map((board: any) => (
                        <div
                          key={board.id}
                          className={cn(
                            'flex items-center space-x-3 p-4 rounded-lg border hover:bg-accent cursor-pointer transition-all',
                            selectedBoardId === board.id ? 'border-primary bg-primary/10 shadow-sm' : 'border-input hover:border-primary/50'
                          )}
                          onClick={() => setSelectedBoardId(board.id)}
                        >
                          <RadioGroupItem
                            value={board.id.toString()}
                            id={`board-${board.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="pointer-events-none"
                          />
                          <Label
                            htmlFor={`board-${board.id}`}
                            className="flex-1 text-sm font-medium leading-snug cursor-pointer"
                          >
                            {board.name}
                          </Label>
                          {selectedBoardId === board.id && (
                            <i className="fa-solid fa-check text-primary" />
                          )}
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </ScrollArea>

                {/* Options Checkboxes - Below Grid */}
                <div className="space-y-3 pt-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="copy-metadata"
                      checked={copyMetadata}
                      onCheckedChange={(checked) => setCopyMetadata(checked as boolean)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="copy-metadata"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        Copy category, tags, status, team and phase selections
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        This option may alter the organization of the target board
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="archive-original"
                      checked={archiveOriginal}
                      onCheckedChange={(checked) => setArchiveOriginal(checked as boolean)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor="archive-original"
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        On the sending board, archive after copy.
                      </Label>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCopy}
              disabled={!selectedBoardId || isLoading}
              className="min-w-[140px]"
            >
              {isLoading ? (
                <>
                  <i className="fa-light fa-spinner fa-spin mr-2" />
                  Copying...
                </>
              ) : (
                <>
                  <i className="fa-light fa-copy mr-2" />
                  Copy to board
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Resolution Modal */}
      <DuplicateEntityResolutionDialog
        open={showDuplicateModal}
        onOpenChange={setShowDuplicateModal}
        boardName={selectedBoardName}
        originalEntityName={displayName}
        entityType={entityType}
        onResolve={handleDuplicateResolve}
        onCancel={handleDuplicateCancel}
        copyMetadata={copyMetadata}
        archiveOriginal={archiveOriginal}
      />
    </>
  );
};
